import { useEffect, useMemo, useState } from 'react';

import { ApiError, apiRequest } from '../lib/api-client';

type ReceiptStatus = 'DRAFT' | 'RECEIVED' | 'CANCELLED';
type PaymentStatus = 'UNPAID' | 'PARTIAL' | 'PAID';
type Supplier = { id: string; name: string; code: string };
type Product = { id: string; name: string; sku: string; sellingPrice: number; costPrice: number };
type ReceiptItem = {
  id: string;
  productId: string;
  quantity: number;
  unitCost: string;
  subtotal: string;
  isFoc: boolean;
  nameSnapshot: string | null;
  product: { id: string; name: string; sku: string; price: string };
};
type Receipt = {
  id: string;
  requestId: string;
  number: string;
  receiptDate: string;
  referenceNumber: string | null;
  status: ReceiptStatus;
  paymentStatus: PaymentStatus;
  paymentMethod: 'CASH' | 'TRANSFER' | 'QRIS' | null;
  paidAmount: string;
  totalAmount: string;
  notes: string | null;
  version: number;
  confirmedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  supplier: Supplier;
  items: ReceiptItem[];
  productCount?: number;
  totalQuantity?: number;
  supplierReturns: Array<{ id: string; number: string; reason: string; totalAmount: string }>;
};
type Line = { key: string; productId: string; quantity: string; unitCost: string; isFoc: boolean };

const rupiah = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
});
const statusStyle: Record<ReceiptStatus, string> = {
  DRAFT: 'bg-slate-100 text-slate-700',
  RECEIVED: 'bg-emerald-50 text-emerald-700',
  CANCELLED: 'bg-red-50 text-red-700',
};
const statusLabel: Record<ReceiptStatus, string> = {
  DRAFT: 'Draft',
  RECEIVED: 'Diterima',
  CANCELLED: 'Dibatalkan',
};
const paymentLabel: Record<PaymentStatus, string> = {
  UNPAID: 'Belum Bayar',
  PARTIAL: 'Sebagian',
  PAID: 'Lunas',
};
const newLine = (): Line => ({
  key: crypto.randomUUID(),
  productId: '',
  quantity: '1',
  unitCost: '',
  isFoc: false,
});

function toCents(value: string) {
  const normalized = value.trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) return null;
  const [whole = '0', fraction = ''] = normalized.split('.');
  return BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
}
function centsText(value: bigint) {
  return `${value / 100n}.${(value % 100n).toString().padStart(2, '0')}`;
}
function displayMoney(value: string) {
  return rupiah.format(Number(value));
}

export function StockReceipts({
  outletReady,
  permissions,
}: {
  outletReady: boolean;
  permissions: string[];
}) {
  const [records, setRecords] = useState<Receipt[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [detail, setDetail] = useState<Receipt | null>(null);
  const [editing, setEditing] = useState<Receipt | null | undefined>(undefined);
  const [supplierId, setSupplierId] = useState('');
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().slice(0, 10));
  const [referenceNumber, setReferenceNumber] = useState('');
  const [lines, setLines] = useState<Line[]>([newLine()]);
  const [paidAmount, setPaidAmount] = useState('0');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const canCreate = permissions.includes('stock_receipts.create');
  const canConfirm = permissions.includes('stock_receipts.confirm');
  const canCancel = permissions.includes('stock_receipts.cancel');
  const canReturn = permissions.includes('stock_returns.create');
  const totalCents = useMemo(
    () =>
      lines.reduce((sum, line) => {
        const cost = toCents(line.unitCost || '0');
        const quantity = Number(line.quantity);
        return cost !== null && Number.isInteger(quantity) && quantity > 0
          ? sum + cost * BigInt(quantity)
          : sum;
      }, 0n),
    [lines],
  );
  const totalQty = lines.reduce(
    (sum, line) => sum + (Number.isInteger(Number(line.quantity)) ? Number(line.quantity) : 0),
    0,
  );

  async function load() {
    if (!outletReady) return;
    try {
      const query = new URLSearchParams({ page: String(page), limit: '10', search });
      if (status) query.set('status', status);
      const result = await apiRequest<{ data: Receipt[]; meta: { total: number } }>(
        `/stock-receipts?${query}`,
      );
      setRecords(result.data);
      setTotal(result.meta.total);
      setError('');
    } catch (value) {
      setError(value instanceof ApiError ? value.message : 'Penerimaan gagal dimuat.');
    }
  }
  useEffect(() => {
    void load();
  }, [outletReady, page, status]);
  useEffect(() => {
    if (!outletReady) return;
    Promise.all([
      apiRequest<{ data: Supplier[] }>('/suppliers?limit=100&status=ACTIVE'),
      apiRequest<{ data: Product[] }>('/products?limit=100'),
    ])
      .then(([supplierResult, productResult]) => {
        setSuppliers(supplierResult.data);
        setProducts(productResult.data);
      })
      .catch(() => {
        setSuppliers([]);
        setProducts([]);
      });
  }, [outletReady]);

  function openForm(record: Receipt | null) {
    setEditing(record);
    setError('');
    setMessage('');
    setSupplierId(record?.supplier.id ?? suppliers[0]?.id ?? '');
    setReceiptDate(record?.receiptDate.slice(0, 10) ?? new Date().toISOString().slice(0, 10));
    setReferenceNumber(record?.referenceNumber ?? '');
    setPaidAmount(record?.paidAmount ?? '0');
    setPaymentMethod(record?.paymentMethod ?? '');
    setNotes(record?.notes ?? '');
    setLines(
      record
        ? record.items.map((item) => ({
            key: item.id,
            productId: item.productId,
            quantity: String(item.quantity),
            unitCost: item.unitCost,
            isFoc: item.isFoc,
          }))
        : [newLine()],
    );
  }

  async function openDetail(id: string) {
    try {
      setDetail(await apiRequest<Receipt>(`/stock-receipts/${id}`));
      setError('');
    } catch (value) {
      setError(value instanceof ApiError ? value.message : 'Detail gagal dimuat.');
    }
  }

  async function save(confirmAfter = false) {
    if (busy) return;
    if (
      !supplierId ||
      !lines.length ||
      lines.some(
        (line) =>
          !line.productId ||
          !Number.isInteger(Number(line.quantity)) ||
          Number(line.quantity) < 1 ||
          toCents(line.unitCost) === null ||
          (line.isFoc ? toCents(line.unitCost)! !== 0n : toCents(line.unitCost)! <= 0n),
      )
    ) {
      setError('Lengkapi supplier dan barang. Biaya normal harus lebih dari Rp0; FOC wajib Rp0.');
      return;
    }
    if (new Set(lines.map((line) => line.productId)).size !== lines.length) {
      setError('Produk tidak boleh muncul dua kali.');
      return;
    }
    if (toCents(paidAmount) === null || toCents(paidAmount)! > totalCents) {
      setError('Jumlah dibayar tidak valid atau melebihi total.');
      return;
    }
    if (toCents(paidAmount)! > 0n && !paymentMethod) {
      setError('Pilih metode pembayaran.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const payload = {
        receiptDate,
        supplierId,
        referenceNumber: referenceNumber.trim() || undefined,
        items: lines.map((line) => ({
          productId: line.productId,
          quantity: Number(line.quantity),
          unitCost: centsText(toCents(line.unitCost)!),
          isFoc: line.isFoc,
        })),
        paidAmount: centsText(toCents(paidAmount)!),
        paymentMethod: paymentMethod || undefined,
        notes: notes.trim() || undefined,
      };
      let record = await apiRequest<Receipt>(`/stock-receipts${editing ? `/${editing.id}` : ''}`, {
        method: editing ? 'PATCH' : 'POST',
        body: JSON.stringify(
          editing
            ? { ...payload, version: editing.version }
            : { ...payload, requestId: crypto.randomUUID() },
        ),
      });
      if (confirmAfter)
        record = await apiRequest<Receipt>(`/stock-receipts/${record.id}/confirm`, {
          method: 'POST',
          body: JSON.stringify({ version: record.version }),
        });
      setEditing(undefined);
      setDetail(record);
      setMessage(
        confirmAfter
          ? 'Penerimaan dikonfirmasi dan stok diperbarui.'
          : 'Draft penerimaan tersimpan.',
      );
      await load();
    } catch (value) {
      setError(value instanceof ApiError ? value.message : 'Penerimaan gagal disimpan.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmReceipt(record: Receipt) {
    if (
      !window.confirm(
        `Konfirmasi ${record.number}? ${record.items.length} produk akan menambah inventory secara atomik.`,
      )
    )
      return;
    setBusy(true);
    try {
      const updated = await apiRequest<Receipt>(`/stock-receipts/${record.id}/confirm`, {
        method: 'POST',
        body: JSON.stringify({ version: record.version }),
      });
      setDetail(updated);
      setMessage('Penerimaan dikonfirmasi.');
      await load();
    } catch (value) {
      setError(value instanceof ApiError ? value.message : 'Konfirmasi gagal.');
    } finally {
      setBusy(false);
    }
  }
  async function cancelReceipt(record: Receipt) {
    const reason = window.prompt('Alasan pembatalan wajib diisi:');
    if (!reason?.trim()) return;
    if (
      !window.confirm(
        'Lanjutkan pembatalan? Untuk penerimaan terkonfirmasi, stok akan dibalik secara atomik.',
      )
    )
      return;
    setBusy(true);
    try {
      const updated = await apiRequest<Receipt>(`/stock-receipts/${record.id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ version: record.version, reason: reason.trim() }),
      });
      setDetail(updated);
      setMessage('Penerimaan dibatalkan.');
      await load();
    } catch (value) {
      setError(value instanceof ApiError ? value.message : 'Pembatalan gagal.');
    } finally {
      setBusy(false);
    }
  }
  async function createReturn(record: Receipt) {
    const items: Array<{ receiptItemId: string; quantity: number }> = [];
    for (const item of record.items) {
      const raw = window.prompt(
        `Jumlah retur ${item.nameSnapshot ?? item.product.name} (maks. ${item.quantity}, kosongkan untuk 0):`,
        '0',
      );
      if (raw === null) return;
      const qty = Number(raw);
      if (!Number.isInteger(qty) || qty < 0 || qty > item.quantity) {
        setError('Jumlah retur tidak valid.');
        return;
      }
      if (qty > 0) items.push({ receiptItemId: item.id, quantity: qty });
    }
    if (!items.length) return;
    const reason = window.prompt('Alasan retur supplier:');
    if (!reason?.trim()) return;
    if (!window.confirm('Konfirmasi retur? Inventory akan berkurang dan tidak boleh negatif.'))
      return;
    setBusy(true);
    try {
      await apiRequest('/stock-returns', {
        method: 'POST',
        body: JSON.stringify({
          requestId: crypto.randomUUID(),
          receiptId: record.id,
          reason: reason.trim(),
          items,
        }),
      });
      await openDetail(record.id);
      setMessage('Retur supplier berhasil dicatat.');
      await load();
    } catch (value) {
      setError(value instanceof ApiError ? value.message : 'Retur gagal.');
    } finally {
      setBusy(false);
    }
  }

  if (!outletReady)
    return (
      <div className="rounded-2xl bg-amber-50 p-6 text-amber-800">
        Pilih outlet untuk membuka penerimaan barang.
      </div>
    );
  return (
    <section className="page-shell">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="font-bold text-ajisai-700">Inventory</p>
          <h1 className="mt-1 text-3xl font-black">Penerimaan Barang</h1>
          <p className="mt-2 text-sm text-slate-500">
            Catat barang jadi dari supplier. Draft tidak mengubah stok.
          </p>
        </div>
        {canCreate && (
          <button
            className="rounded-xl bg-ajisai-700 px-5 py-3 font-bold text-white"
            onClick={() => openForm(null)}
          >
            + Penerimaan Baru
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
          {error}
        </p>
      )}
      {message && (
        <p className="mt-5 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-700">{message}</p>
      )}
      <div className="mt-6 overflow-hidden rounded-2xl border bg-white">
        <form
          className="flex flex-col gap-2 border-b p-4 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            void load();
          }}
        >
          <input
            className="flex-1 rounded-xl border p-3"
            placeholder="Nomor, supplier, referensi, produk"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="rounded-xl border p-3"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Semua status</option>
            <option value="DRAFT">Draft</option>
            <option value="RECEIVED">Diterima</option>
            <option value="CANCELLED">Dibatalkan</option>
          </select>
          <button className="rounded-xl bg-slate-900 px-5 text-white">Cari</button>
        </form>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-5 py-3">Nomor / tanggal</th>
                <th>Supplier</th>
                <th>Barang</th>
                <th>Nilai</th>
                <th>Pembayaran</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {records.map((record) => (
                <tr key={record.id}>
                  <td className="px-5 py-4">
                    <b>{record.number}</b>
                    <p>{new Date(record.receiptDate).toLocaleDateString('id-ID')}</p>
                  </td>
                  <td>{record.supplier.name}</td>
                  <td>
                    {record.productCount} produk · {record.totalQuantity} pcs
                  </td>
                  <td>{displayMoney(record.totalAmount)}</td>
                  <td>{paymentLabel[record.paymentStatus]}</td>
                  <td>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-bold ${statusStyle[record.status]}`}
                    >
                      {statusLabel[record.status]}
                    </span>
                  </td>
                  <td className="px-5 text-right">
                    <button
                      className="font-bold text-ajisai-700"
                      onClick={() => void openDetail(record.id)}
                    >
                      Detail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!records.length && (
          <p className="p-12 text-center text-sm text-slate-500">Belum ada penerimaan.</p>
        )}
        <div className="flex justify-between border-t p-4 text-sm">
          <span>{total} penerimaan</span>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage((v) => v - 1)}>
              Sebelumnya
            </button>
            <button disabled={page * 10 >= total} onClick={() => setPage((v) => v + 1)}>
              Berikutnya
            </button>
          </div>
        </div>
      </div>
      {editing !== undefined && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/50 p-4">
          <form
            className="mx-auto my-4 max-w-4xl rounded-2xl bg-white p-6"
            onSubmit={(event) => {
              event.preventDefault();
              void save(false);
            }}
          >
            <div className="flex justify-between">
              <div>
                <p className="font-bold text-ajisai-700">
                  {editing ? 'Edit Draft' : 'Penerimaan Baru'}
                </p>
                <h2 className="text-2xl font-black">
                  {editing?.number ?? 'Barang jadi dari supplier'}
                </h2>
              </div>
              <button type="button" onClick={() => setEditing(undefined)}>
                ×
              </button>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-bold">
                Tanggal
                <input
                  required
                  type="date"
                  className="mt-2 w-full rounded-xl border p-3"
                  value={receiptDate}
                  onChange={(e) => setReceiptDate(e.target.value)}
                />
              </label>
              <label className="text-sm font-bold">
                Supplier
                <select
                  required
                  className="mt-2 w-full rounded-xl border p-3"
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                >
                  <option value="">Pilih supplier</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-bold sm:col-span-2">
                Nomor referensi (opsional)
                <input
                  className="mt-2 w-full rounded-xl border p-3"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                />
              </label>
            </div>
            <div className="mt-6 space-y-3">
              <div className="flex justify-between">
                <h3 className="font-black">Barang</h3>
                <button
                  type="button"
                  className="font-bold text-ajisai-700"
                  onClick={() => setLines([...lines, newLine()])}
                >
                  + Tambah baris
                </button>
              </div>
              {lines.map((line, index) => (
                <div
                  key={line.key}
                  className="grid gap-2 rounded-xl border p-3 sm:grid-cols-[2fr_.7fr_1fr_auto_auto]"
                >
                  <select
                    required
                    className="rounded-lg border p-2"
                    value={line.productId}
                    onChange={(e) =>
                      setLines(
                        lines.map((v, i) =>
                          i === index
                            ? {
                                ...v,
                                productId: e.target.value,
                                unitCost: String(
                                  products.find((p) => p.id === e.target.value)?.costPrice ?? '',
                                ),
                              }
                            : v,
                        ),
                      )
                    }
                  >
                    <option value="">Pilih produk</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.sku} · {product.name} (jual {rupiah.format(product.sellingPrice)})
                      </option>
                    ))}
                  </select>
                  <input
                    required
                    type="number"
                    min="1"
                    className="rounded-lg border p-2"
                    value={line.quantity}
                    onChange={(e) =>
                      setLines(
                        lines.map((v, i) => (i === index ? { ...v, quantity: e.target.value } : v)),
                      )
                    }
                  />
                  <input
                    required
                    inputMode="decimal"
                    className="rounded-lg border p-2"
                    placeholder="Biaya/unit"
                    disabled={line.isFoc}
                    value={line.unitCost}
                    onChange={(e) =>
                      setLines(
                        lines.map((v, i) => (i === index ? { ...v, unitCost: e.target.value } : v)),
                      )
                    }
                  />
                  <label className="flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={line.isFoc}
                      onChange={(e) =>
                        setLines(
                          lines.map((v, i) =>
                            i === index
                              ? {
                                  ...v,
                                  isFoc: e.target.checked,
                                  unitCost: e.target.checked ? '0' : '',
                                }
                              : v,
                          ),
                        )
                      }
                    />{' '}
                    FOC
                  </label>
                  <button
                    type="button"
                    disabled={lines.length === 1}
                    onClick={() => setLines(lines.filter((_, i) => i !== index))}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-bold">
                Jumlah dibayar
                <input
                  inputMode="decimal"
                  className="mt-2 w-full rounded-xl border p-3"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                />
              </label>
              <label className="text-sm font-bold">
                Metode pembayaran
                <select
                  className="mt-2 w-full rounded-xl border p-3"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <option value="">Belum dibayar</option>
                  <option value="CASH">Tunai</option>
                  <option value="TRANSFER">Transfer</option>
                  <option value="QRIS">QRIS</option>
                </select>
              </label>
              <label className="text-sm font-bold sm:col-span-2">
                Catatan
                <textarea
                  className="mt-2 w-full rounded-xl border p-3"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </label>
            </div>
            <div className="mt-6 rounded-xl bg-slate-50 p-4">
              <p className="font-black">Ringkasan sebelum konfirmasi</p>
              <div className="mt-2 grid grid-cols-3 gap-3 text-sm">
                <span>{lines.length} produk</span>
                <span>{totalQty} pcs</span>
                <b>{displayMoney(centsText(totalCents))}</b>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Harga jual hanya informasi dari Master Produk dan tidak berubah karena penerimaan.
              </p>
            </div>
            {error && <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="rounded-xl border px-5 py-3 font-bold"
                onClick={() => setEditing(undefined)}
              >
                Batal
              </button>
              <button
                disabled={busy}
                className="rounded-xl border border-ajisai-700 px-5 py-3 font-bold text-ajisai-700"
              >
                Simpan Draft
              </button>
              {canConfirm && (
                <button
                  type="button"
                  disabled={busy}
                  className="rounded-xl bg-ajisai-700 px-5 py-3 font-bold text-white"
                  onClick={() => void save(true)}
                >
                  Konfirmasi Penerimaan
                </button>
              )}
            </div>
          </form>
        </div>
      )}
      {detail && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/50 p-4">
          <div className="mx-auto my-4 max-w-3xl rounded-2xl bg-white p-6">
            <div className="flex justify-between">
              <div>
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-bold ${statusStyle[detail.status]}`}
                >
                  {statusLabel[detail.status]}
                </span>
                <h2 className="mt-2 text-2xl font-black">{detail.number}</h2>
                <p className="text-sm text-slate-500">
                  {detail.supplier.name} ·{' '}
                  {new Date(detail.receiptDate).toLocaleDateString('id-ID')}
                </p>
              </div>
              <button onClick={() => setDetail(null)}>×</button>
            </div>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[600px] text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2">Produk</th>
                    <th>Qty</th>
                    <th>Biaya</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.items.map((item) => (
                    <tr className="border-b" key={item.id}>
                      <td className="py-3">
                        <b>{item.nameSnapshot ?? item.product.name}</b>
                        <p className="text-xs">
                          {item.product.sku}
                          {item.isFoc ? ' · BONUS/FOC' : ''}
                        </p>
                      </td>
                      <td>{item.quantity}</td>
                      <td>{displayMoney(item.unitCost)}</td>
                      <td>{displayMoney(item.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <dl className="mt-5 grid gap-3 rounded-xl bg-slate-50 p-4 sm:grid-cols-3">
              <div>
                <dt className="text-xs text-slate-500">Total</dt>
                <dd className="font-black">{displayMoney(detail.totalAmount)}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">Dibayar</dt>
                <dd className="font-black">{displayMoney(detail.paidAmount)}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">Status bayar</dt>
                <dd className="font-black">{paymentLabel[detail.paymentStatus]}</dd>
              </div>
            </dl>
            {detail.cancelReason && (
              <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">
                Alasan: {detail.cancelReason}
              </p>
            )}
            {detail.supplierReturns.length > 0 && (
              <section className="mt-5 rounded-xl border border-slate-200 p-4">
                <h3 className="font-black">Riwayat Retur Supplier</h3>
                <div className="mt-3 space-y-2">
                  {detail.supplierReturns.map((record) => (
                    <div
                      key={record.id}
                      className="flex flex-col justify-between gap-1 rounded-lg bg-slate-50 p-3 text-sm sm:flex-row"
                    >
                      <span>
                        <b>{record.number}</b> · {record.reason}
                      </span>
                      <b>{displayMoney(record.totalAmount)}</b>
                    </div>
                  ))}
                </div>
              </section>
            )}
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              {detail.status === 'DRAFT' && canCreate && (
                <button
                  className="rounded-xl border px-4 py-2 font-bold"
                  onClick={() => {
                    setDetail(null);
                    openForm(detail);
                  }}
                >
                  Edit Draft
                </button>
              )}
              {detail.status === 'DRAFT' && canConfirm && (
                <button
                  disabled={busy}
                  className="rounded-xl bg-ajisai-700 px-4 py-2 font-bold text-white"
                  onClick={() => void confirmReceipt(detail)}
                >
                  Konfirmasi
                </button>
              )}
              {detail.status === 'RECEIVED' && canReturn && (
                <button
                  disabled={busy}
                  className="rounded-xl border px-4 py-2 font-bold"
                  onClick={() => void createReturn(detail)}
                >
                  Retur Supplier
                </button>
              )}
              {detail.status !== 'CANCELLED' && canCancel && (
                <button
                  disabled={busy}
                  className="rounded-xl border border-red-300 px-4 py-2 font-bold text-red-700"
                  onClick={() => void cancelReceipt(detail)}
                >
                  Batalkan
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
